import RouteTemplate from 'ember-route-template'
import type { TOC } from '@ember/component/template-only'
import { get } from '@ember/helper'
import { fn } from '@ember/helper'
import { array } from '@ember/helper'
import { on } from '@ember/modifier'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { t } from 'ember-intl'
import InputText from 'teacher-ui-client/components/base/input-text'
import RadioGroup from 'teacher-ui-client/components/base/radio-group'
import GradeSelect from 'teacher-ui-client/components/base/select/grade-position'
import SchoolClassSelect from 'teacher-ui-client/components/base/select/school-class'
import TeacherSelect from 'teacher-ui-client/components/base/select/teacher'
import FormAddStudentsToClass from 'teacher-ui-client/components/form/add-students-to-class'
import LayoutBlocksModal from 'teacher-ui-client/components/layout-blocks/modal'
import PageHeader from 'teacher-ui-client/components/page-header'
import PageHeaderReturnLink from 'teacher-ui-client/components/page-header/return-link'
import UiButton from 'teacher-ui-client/components/ui-button'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import includes from 'teacher-ui-client/helpers/includes'
import joinList from 'teacher-ui-client/helpers/join-list'
import pick from 'teacher-ui-client/helpers/pick'
import themeKey from 'teacher-ui-client/helpers/theme-key'
import usesExternalAuthentication from 'teacher-ui-client/helpers/uses-external-authentication'
import type MoveStudentsController from 'teacher-ui-client/manage/move-students/controller'
import type { MoveStudentsModel } from 'teacher-ui-client/manage/move-students/route'
import DataOperationsTable from 'teacher-ui-client/components/data-operations/table'
import { SortType } from 'teacher-ui-client/components/data-operations/table/sort-definition'

interface Signature {
  Args: {
    model: MoveStudentsModel
    controller: MoveStudentsController
  }
}

const MoveStudentsPage: TOC<Signature> = <template>
  <PageHeader>
    <:title>
      <h1>
        {{t "manage.manageClasses.students.moveStudents.title"}}
      </h1>
    </:title>
    <:left>
      <PageHeaderReturnLink @route="manage.students">
        {{t "manage.manageClasses.returnLink"}}
      </PageHeaderReturnLink>
    </:left>
  </PageHeader>
  <DataOperationsTable @dataOperationsConfig={{@controller.dataOperationsConfig}} as |selectRecords sorting pagination|>
    <div class="border-dusty-black-100 rounded-md border">
      <div class="flex flex-wrap items-center justify-between gap-3 p-3 {{@controller.themeClasses.actionsHeaderBg}}">
        <div class="flex flex-wrap items-center gap-3">
          <label>{{t "manage.manageClasses.filterByGroupLabel"}}</label>
          <SchoolClassSelect
            class="max-w-xs truncate"
            @additionalOptions={{array @controller.AllClassesOption}}
            @schoolClasses={{@controller.schoolClassesForSelect}}
            @schoolClassId={{@controller.schoolClassesFilterValue}}
            @updateSchoolClassId={{@controller.updateSchoolClassId}}
            @themeKey={{themeKey @model.subscriptionType}}
          />
          <TeacherSelect
            class="max-w-xs truncate"
            @additionalOptions={{array @controller.AllTeachersOption}}
            @teachers={{@controller.teachersForSelect}}
            @teacherId={{@controller.teachersFilterValue}}
            @updateTeacherId={{@controller.updateTeacherId}}
            @themeKey={{themeKey @model.subscriptionType}}
          />
          <GradeSelect
            class="max-w-xs truncate"
            @additionalOptions={{array @controller.AllGradesOption}}
            @gradePositions={{@controller.gradePositionsForSelect}}
            @gradePosition={{@controller.gradesFilterValue}}
            @updateGradePosition={{@controller.updateGradePosition}}
            @themeKey={{themeKey @model.subscriptionType}}
          />
        </div>
        <div class="flex flex-wrap items-center gap-3">
          <label>{{t "manage.manageClasses.filterByGroupLabel"}}</label>
          <RadioGroup
            @listClass="flex gap-2"
            @optionSet={{@controller.allocationOptionSet}}
            @checkedOptionId={{@controller.allocationFilterValue}}
            @onSelectionChange={{@controller.updateAllocationFilterValue}}
          />
        </div>
      </div>
      <div class="flex flex-wrap items-center justify-between gap-3 p-3 {{@controller.themeClasses.actionsHeaderBg}}">
        <UiButton
          data-test-add-to-class
          disabled={{selectRecords.noneSelected}}
          {{on "click" (fn @controller.showAddToClassModal true)}}
        >
          {{t @controller.addToClassTextKey}}
        </UiButton>

        <InputText
          type="search"
          role="searchbox"
          name="search-filter"
          value={{@controller.textFilter}}
          placeholder={{t "manage.manageClasses.students.filterStudents.placeholder"}}
          aria-label={{t "manage.manageClasses.students.filterStudents.label"}}
          @themeKey={{@controller.themeKey}}
          @icon="search"
          @iconEnd={{true}}
          {{on "input" (pick "target.value" @controller.onSearchInput)}}
        />
      </div>
      <div class="w-full overflow-auto print:overflow-hidden">
        <table data-test-manage-move-students-table class="w-full border-t border-white print:table-auto">
          <thead>
            <tr class="divide-x divide-white border-b border-white {{@controller.themeClasses.tableHeaderBg}}">
              <th class="w-0 p-0 text-center">
                <label class="cursor-pointer p-2 select-none">
                  {{t "manage.selectAllLabel"}}
                  <input
                    data-test-select-all-students
                    class="cursor-pointer"
                    type="checkbox"
                    checked={{selectRecords.allSelected}}
                    {{on "change" selectRecords.toggleAll}}
                  />
                </label>
              </th>
              <th>
                <button
                  type="button"
                  class="w-full px-3 py-6 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByValue "firstName" null)}}
                >
                  {{t "manage.firstName"}}
                  <PhosphorIcon @icon="phosphor-caret-up-down-fill" />
                </button>
              </th>
              <th>
                <button
                  type="button"
                  class="w-full px-3 py-6 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByValue "lastName" null)}}
                >
                  {{t "manage.lastName"}}
                  <PhosphorIcon @icon="phosphor-caret-up-down-fill" />
                </button>
              </th>
              {{#unless (usesExternalAuthentication)}}
                <th data-test-login-header>
                  <button
                    type="button"
                    class="w-full px-3 py-6 text-left whitespace-nowrap"
                    {{on "click" (fn sorting.sort SortType.ByValue "login" null)}}
                  >
                    {{t "manage.login"}}
                    <PhosphorIcon @icon="phosphor-caret-up-down-fill" />
                  </button>
                </th>
              {{/unless}}
              <th>
                <button
                  type="button"
                  class="w-full px-3 py-6 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByValue "schoolClassNames" null)}}
                >
                  {{t "manage.manageClasses.students.moveStudents.table.headers.classes"}}
                  <PhosphorIcon @icon="phosphor-caret-up-down-fill" />
                </button>
              </th>
              <th>
                <button
                  type="button"
                  class="w-full px-3 py-6 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByValue "teacherNames" null)}}
                >
                  {{t "manage.manageClasses.students.moveStudents.table.headers.teachers"}}
                  <PhosphorIcon @icon="phosphor-caret-up-down-fill" />
                </button>
              </th>
              <th>
                <button
                  type="button"
                  class="w-full px-3 py-6 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByGrade "gradePosition" null)}}
                >
                  {{t "manage.manageClasses.students.moveStudents.table.headers.grade"}}
                  <PhosphorIcon @icon="phosphor-caret-up-down-fill" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {{#each pagination.currentPageRecords as |row|}}
              <tr class={{@controller.themeClasses.tableRowBg}}>
                <td class="w-0 p-0 text-center">
                  <label
                    class="cursor-pointer p-2"
                    aria-label={{t "manage.manageClasses.selectSingleRecordLabel" name=row.fullName}}
                  >
                    <input
                      type="checkbox"
                      class="cursor-pointer"
                      checked={{includes selectRecords.selectedIds row.id}}
                      {{on "change" (fn selectRecords.toggle row.id)}}
                    />
                  </label>
                </td>
                <td class="p-2">
                  {{row.firstName}}
                </td>
                <td class="p-2">
                  {{row.lastName}}
                </td>
                {{#unless (usesExternalAuthentication)}}
                  <td class="p-2">{{row.login}}</td>
                {{/unless}}
                <td class="p-2">
                  {{joinList (get row @controller.schoolClassNamesKey) ", "}}
                </td>
                <td class="p-2">
                  {{joinList (get row @controller.teacherNamesKey) ", "}}
                </td>
                <td class="p-2">
                  {{gradeName row.gradePosition}}
                </td>
              </tr>
            {{else}}
              <tr>
                <td class="p-2 text-center italic" colspan={{if (usesExternalAuthentication) "6" "7"}}>
                  {{t "manage.noDataAvailable"}}
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </div>
    <LayoutBlocksModal
      @title={{t @controller.addToClassTextKey}}
      @showModal={{@controller.addToClassModalOpen}}
      @close={{fn @controller.showAddToClassModal false}}
    >
      <FormAddStudentsToClass
        @students={{selectRecords.selectedRecords}}
        @schoolClasses={{@controller.allSchoolClassesForSubscriptionType}}
        @subscriptionType={{@model.subscriptionType}}
        @onSuccess={{@controller.studentsAddedToClass}}
        @onCancel={{fn @controller.showAddToClassModal false}}
      />
    </LayoutBlocksModal>
  </DataOperationsTable>
</template>

export default RouteTemplate(MoveStudentsPage)
